import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';
import { cn } from '../../lib/cn';

const rowButtonVariants = cva('flex w-full cursor-pointer text-left', {
  variants: {
    variant: {
      'list-row':
        'items-center gap-2 rounded-sm py-2 pr-1 pl-1.5 hover:bg-black/3 focus:outline-none focus-visible:bg-neutral-100',
      'task-row':
        'items-start gap-2 rounded-md p-2 hover:bg-black/3 focus:outline-none focus-visible:bg-neutral-100',
      'summary-line':
        'text-text-primary items-center justify-between gap-3 rounded-lg bg-neutral-50 py-2 pr-5 pl-4 text-base',
    },
  },
});

type RowButtonProps = React.ComponentProps<'button'> & {
  /**
   * - `list-row`: a row in a list, such as a medication. It has no background, so the row's own
   *   background shows through. It gets a tint on hover.
   * - `task-row`: like `list-row`, with the content aligned to the top for a leading icon
   *   beside two lines of text.
   * - `summary-line`: a filled line above a list, such as the freshness line.
   */
  variant: NonNullable<VariantProps<typeof rowButtonVariants>['variant']>;
};

/**
 * A full-width button whose content is a whole row, not a short text label. Give it an
 * `aria-label` when the row text is too long to be its accessible name. Use it as the `render`
 * element of a `PopoverTrigger` or `DialogTrigger`, or alone with `onClick`.
 */
export function RowButton({ variant, className, type = 'button', ...props }: RowButtonProps) {
  return (
    <button
      type={type}
      data-slot="row-button"
      className={cn(rowButtonVariants({ variant }), className)}
      {...props}
    />
  );
}
